import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useUrlState } from "@/hooks/useUrlState";
import { BackButton } from "@/hooks/useBackNavigation";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TestBadge, EmptyState, WhatsAppIcon } from "@/components/common";
import { QuickWhatsAppDialog, EmailDialog } from "@/components/QuickComms";
import { CallLogDialog } from "@/pages/CallsPage";
import { DocumentsWorkspace } from "@/components/DocumentsWorkspace";
import { TagsEditor } from "@/components/TagsEditor";
import { AdmissionOutcomeActions } from "@/components/AdmissionOutcomeActions";
import { VisaOutcomeActions } from "@/components/VisaOutcomeActions";
import { WorkflowOutcomeDialog } from "@/components/WorkflowOutcomeDialog";
import { NextActionCard } from "@/components/NextActionCard";
import { WhatHappensNextPanel } from "@/components/WhatHappensNextPanel";
import { ProcessOwnerPicker, loadAssignableOwners } from "@/components/ProcessOwnerPicker";
import { CaseTimelineFeed } from "@/components/CaseTimelineFeed";
import { WorkflowSetupRequiredNotice, isWorkflowSetupBlock } from "@/components/WorkflowSetupRequired";
import { StageGateExplanation } from "@/components/StageGateExplanation";
import { gateView } from "@/lib/stageGate";
import { RecordObligationPaymentDialog, ObligationPaymentsBlock } from "@/components/ObligationPayment";
import {
  Plus, ArrowRight, PhoneCall, Mail, Lock, AlertTriangle, Briefcase,
} from "lucide-react";

const STATUS_CLS = {
  active: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
  waiting_customer: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  at_risk: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  discontinued: "bg-muted text-muted-foreground",
};

const WAITING_REASONS = [
  { key: "customer", label: "Waiting for customer" },
  { key: "documents", label: "Waiting for documents" },
  { key: "payment", label: "Waiting for payment" },
  { key: "external_party", label: "Waiting for external party" },
];

const TABS = [
  ["overview", "Overview"], ["profile", "Profile"], ["stages", "Stages & Milestones"],
  ["tasks", "Tasks"], ["communications", "Communications"], ["calls", "Calls"],
  ["meetings", "Meetings"], ["notes", "Notes"], ["documents", "Documents"],
  ["info_requests", "Info Requests"], ["payments", "Payments"],
  ["tickets", "Tickets"], ["timeline", "Timeline"], ["audit", "Audit"],
];

const Field = ({ label, children, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-sm break-words">{children ?? "—"}</div>
  </div>
);

const Panel = ({ title, children, testId }) => (
  <div className="rounded-md border p-3 space-y-1.5 text-xs" data-testid={testId}>
    {title && <div className="font-semibold text-muted-foreground">{title}</div>}
    {children}
  </div>
);

export default function ProcessCaseDetailPage() {
  const { caseId } = useParams();
  const location = useLocation();
  const { user, hasCap } = useAuth();
  // Same capability pair the server's PATCH /cases/{id}/process-owner gate
  // uses (routers_platform.CASE_REASSIGN_CAPS) — the reassign controls are
  // only offered to people it would accept.
  const canReassign = hasCap("process.cases.reassign") || hasCap("crm.assign");
  // The active tab lives in the URL too, so Back/refresh/a shared link all
  // restore the exact section the user was reading — same useUrlState
  // mechanism the list page uses for its own filter state.
  // outcome_task / origin_task_id — the Process workflow outcome form for
  // ONE exact pending task, URL-backed so a reload or a link from the Tasks
  // page (which appends origin_task_id) reopens the same task's form.
  const [{ tab, outcome_task: outcomeTask, origin_task_id: originTaskId, tl_page: tlPage }, setTabState] =
    useUrlState({ tab: "overview", outcome_task: "", origin_task_id: "", tl_page: 1 });
  const openOutcomeTaskId = outcomeTask || originTaskId;
  const [wf, setWf] = useState(null);
  const [reverseTo, setReverseTo] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [data, setData] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState(null);
  const [auditDenied, setAuditDenied] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: "normal", due_at: "" });
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassign, setReassign] = useState({ owner: "", reason: "" });
  const [waitingOpen, setWaitingOpen] = useState(false);
  const [waitingReason, setWaitingReason] = useState("customer");
  const [blocked, setBlocked] = useState(null);
  // R13 Workstream 7: persistent explanation of a refused stage move (shared with the Pipelines
  // table / Kanban and Customer 360).
  const [gateExplain, setGateExplain] = useState(null);
  const [gateAction, setGateAction] = useState(null); // {label, run}
  const navigate = useNavigate();
  // 2026-09 R4 — the distinct, non-overridable "Workflow setup required"
  // block (never merged into `blocked`, which explains business gates).
  const [setupBlocked, setSetupBlocked] = useState(null);
  // R5 (gap G1) — workflow-raised add-on charges for THIS Case (e.g. the
  // Partnership IELTS/PTE class fee) + their payments, shown and payable
  // on the Payments tab. Same finance.view / finance.record /
  // finance.verify gates as Customer 360 Finance.
  const canViewFinance = hasCap("finance.view");
  const canRecordPayment = hasCap("finance.record");
  const canVerifyPayment = hasCap("finance.verify");
  const [addons, setAddons] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const loadAddons = useCallback(() => {
    if (!caseId || !canViewFinance) return;
    api.get(`/finance/purpose-obligations/by-case/${caseId}`).then((r) => setAddons(r.data))
      .catch(() => setAddons({ obligations: [], payments: [] }));
  }, [caseId, canViewFinance]);
  useEffect(() => { if (tab === "payments") loadAddons(); }, [tab, loadAddons]);
  const verifyAddonPayment = async (paymentId) => {
    try {
      await api.post(`/finance/payments/${paymentId}/verify`, { approve: true });
      toast.success("Payment verified"); loadAddons(); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const load = useCallback(() => {
    if (!caseId) return;
    api.get(`/cases/${caseId}`).then((r) => setData(r.data)).catch((e) => {
      toast.error(errDetail(e, "Could not load this case")); setData({ error: true });
    });
    api.get(`/cases/${caseId}/context`).then((r) => setCtx(r.data)).catch(() => {});
    api.get(`/cases/${caseId}/workflow`).then((r) => setWf(r.data)).catch(() => setWf(null));
  }, [caseId]);
  const openOutcome = (taskId) => setTabState({ outcome_task: taskId || "", origin_task_id: "" });
  const closeOutcome = () => setTabState({ outcome_task: "", origin_task_id: "" });

  useEffect(() => { load(); }, [load]);
  // Reassign candidates = the server's one eligibility rule (GET
  // /cases/assignable-owners), shared with ProcessOwnerPicker — never the raw
  // user list (which offered counsellors/back office the server then refused).
  useEffect(() => {
    if (!canReassign) return;
    loadAssignableOwners().then(setUsers).catch(() => setUsers([]));
  }, [canReassign]);
  useEffect(() => {
    if (tab !== "audit" || audit || auditDenied) return;
    api.get(`/admin/audit?record_id=${caseId}&limit=100`)
      .then((r) => setAudit(r.data))
      .catch((e) => { if (e?.response?.status === 403) setAuditDenied(true); else setAudit([]); });
  }, [tab, caseId, audit, auditDenied]);

  const c = data?.case;
  const h = data?.header || {};
  const contact = ctx?.contact || data?.contact;
  const gate = h.signup_gate;
  const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);

  const move = async (stage, extra = {}) => {
    setBlocked(null);
    try {
      await api.post(`/cases/${caseId}/stage`, { stage, ...extra });
      toast.success(`Case → ${stage}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Not mapped onto the canonical workflow: shown as its own notice
      // (admin-only way forward, no override) — never a generic error toast.
      if (isWorkflowSetupBlock(d)) {
        setSetupBlocked(d);
        return;
      }
      // Registry-governed Case: a forward move needs its outcome — the
      // server returns the ONE canonical pending task; open its form.
      if (d && typeof d === "object" && d.blocker_type === "outcome_required" && d.task_id) {
        // Explained first (which outcome, which rule); the existing outcome form is one click away.
        setGateExplain(gateView(e, { requested: stage, current: c?.current_stage,
                                     subject: contact?.full_name || caseId }));
        setGateAction({ label: "Open the outcome form", run: () => openOutcome(d.task_id) });
        return;
      }
      if (d && typeof d === "object" && d.blocker_type === "reversal_reason_required") {
        setReverseTo(stage); setReverseReason("");
        return;
      }
      if (d && typeof d === "object" && ["required_payment", "required_approval",
        "system_managed_stage", "transition_not_permitted"].includes(d.blocker_type)) {
        setBlocked(d);
      }
      // A structured, non-overridable business gate explains itself
      // in-page rather than as a disappearing toast — the user needs to
      // read WHY and what would unblock it.
      if (d && typeof d === "object" && d.blocker_type === "required_approval"
          && d.overridable === false) {
        setBlocked(d);
      }
      // Every other refusal (structured or not) is explained in the shared dialog, never only by a
      // toast that vanishes before it can be read. The record was not changed.
      setGateExplain(gateView(e, { requested: stage, current: c?.current_stage,
                                   subject: contact?.full_name || caseId }));
    }
  };

  const addNote = async () => {
    try {
      await api.post(`/cases/${caseId}/notes`, { body: noteText });
      setNoteText(""); load(); toast.success("Case note added (append-only)");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const addTask = async () => {
    try {
      await api.post(`/cases/${caseId}/tasks`, {
        title: newTask.title, description: newTask.description || null,
        priority: newTask.priority,
        due_at: newTask.due_at ? new Date(newTask.due_at).toISOString() : null,
      });
      setNewTask({ title: "", description: "", priority: "normal", due_at: "" });
      setTaskDialogOpen(false); load(); toast.success("Task added");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const doReassign = async () => {
    try {
      await api.patch(`/cases/${caseId}/process-owner`, {
        process_owner_user_id: reassign.owner || null,
        expected_current_owner_user_id: h.process_owner_user_id || null,
        reason: reassign.reason || null,
      });
      toast.success("Process owner reassigned");
      setReassignOpen(false); setReassign({ owner: "", reason: "" }); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setPriority = async (p) => {
    try {
      await api.patch(`/cases/${caseId}/priority`, { priority: p });
      toast.success(`Priority → ${p}`); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (data?.error) {
    return (
      <div className="space-y-4" data-testid="case-detail-page">
        <BackButton fallbackRoute="/process" />
        <EmptyState icon={Briefcase} title="Case not available"
          hint="It may not exist, or you may not have permission to view it."
          testId="case-detail-error" />
      </div>
    );
  }
  if (!data || !c) {
    return (
      <div className="space-y-4" data-testid="case-detail-page">
        <BackButton fallbackRoute="/process" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const stagesBlocked = new Set(gate?.commercial_signup_complete === false
    ? (gate?.stages_requiring_signup || []) : []);
  const governed = !!h.governed;
  // `actionable` (server-derived, R5): open AND in the Case's current stage
  // (or declared carry-forward) — never an open round of a stage the Case left.
  const wfTasks = (wf?.tasks || []).filter((t) => (t.actionable ?? ["open", "in_progress", "waiting"].includes(t.status)));
  const stageClick = (s) => {
    const cur = (c.stages || []).indexOf(c.current_stage);
    const idx = (c.stages || []).indexOf(s);
    if (governed && idx < cur) { setReverseTo(s); setReverseReason(""); return; }
    move(s);
  };

  return (
    <div className="space-y-4" data-testid="case-detail-page">
      <BackButton fallbackRoute="/process" testId="case-back-button" />

      {/* ------------------------------ HEADER ------------------------------ */}
      <Card className="p-4 space-y-3" data-testid="case-header">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-heading text-xl font-semibold tracking-tight truncate">
              {h.customer_name || contact?.full_name || "Case"}
            </h1>
            <p className="text-xs text-muted-foreground font-mono">
              {h.customer_id} · Case {h.case_id}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap ml-auto">
            <Badge variant="outline" className={STATUS_CLS[c.status] || ""} data-testid="case-status-badge">
              {String(c.status).replace(/_/g, " ")}
            </Badge>
            {h.priority && h.priority !== "normal" && (
              <Badge variant="outline" className="capitalize" data-testid="case-priority-badge">{h.priority} priority</Badge>
            )}
            {h.is_overdue && <Badge variant="outline" className="bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]" data-testid="case-overdue-badge">Overdue</Badge>}
            <TestBadge show={c.is_test_record} />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-3">
          <Field label="Product" testId="case-hdr-product">{h.product_code}</Field>
          <Field label="Opportunity" testId="case-hdr-opportunity">
            {h.opportunity_id ? (
              <span title={h.opportunity_id}>{h.opportunity_stage || "—"}</span>
            ) : "—"}
          </Field>
          <Field label="Current stage" testId="case-hdr-stage">{h.current_stage}</Field>
          <Field label="Stage age" testId="case-hdr-stage-age">
            {h.stage_age_days != null ? `${h.stage_age_days} day${h.stage_age_days === 1 ? "" : "s"}` : "—"}
          </Field>
          {/* The two owners are deliberately rendered as SEPARATE, clearly
              labelled fields — they are different people on different
              records and must never read as one "Owner". */}
          <Field label="Process owner" testId="case-hdr-process-owner">
            {canReassign ? (
              <ProcessOwnerPicker caseId={c.case_id} ownerId={h.process_owner_user_id}
                ownerName={h.process_owner_name} testId="case-hdr-owner-picker"
                onAssigned={(r) => { if (!r.optimistic) load(); }} />
            ) : (h.process_owner_name || <span className="text-muted-foreground">Unassigned</span>)}
          </Field>
          <Field label="Sales owner" testId="case-hdr-sales-owner">
            {h.sales_owner_name || <span className="text-muted-foreground">Unassigned</span>}
          </Field>
          <Field label="Team / unit" testId="case-hdr-team">{h.division}</Field>
          <Field label="Next deadline" testId="case-hdr-deadline">
            {/* One derivation (backend case_reconciliation.derive_deadline): the active governed
                task's due date — the same task the Next Action offers. No task, no deadline. */}
            {h.next_deadline
              ? <span title={h.deadline_source === "governed_task" ? "Due date of the active workflow task" : undefined}>{fmtIST(h.next_deadline)}</span>
              : <span className="text-muted-foreground" title={h.deadline_source || undefined}>No active deadline</span>}
          </Field>
          <Field label="Waiting / block reason" testId="case-hdr-waiting">
            {h.waiting_reason
              ? (WAITING_REASONS.find((w) => w.key === h.waiting_reason)?.label || h.waiting_reason)
              : "—"}
          </Field>
          <Field label="Payment state" testId="case-hdr-payment">
            {h.payment_state?.totals
              ? `${inr(h.payment_state.totals.total_paid)} / ${inr(h.payment_state.totals.total_agreed)}`
              : "No plan"}
          </Field>
          <Field label="Risk" testId="case-hdr-risk">
            {(() => {
              const r = h.risk_indicators || {};
              const bits = [];
              if (r.overdue_case) bits.push("case overdue");
              if (r.overdue_tasks) bits.push(`${r.overdue_tasks} overdue task(s)`);
              if (r.missing_documents) bits.push(`${r.missing_documents} doc gap(s)`);
              if (r.at_risk) bits.push("at risk");
              if (r.npa) bits.push("NPA");
              return bits.length ? bits.join(", ") : "None";
            })()}
          </Field>
        </div>

        {/* --------------------------- QUICK ACTIONS -------------------------- */}
        <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid="case-quick-actions">
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-call" onClick={() => setCallOpen(true)}>
            <PhoneCall className="h-3.5 w-3.5 mr-1.5" />Call
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-whatsapp" onClick={() => setWaOpen(true)}>
            <WhatsAppIcon className="h-3.5 w-3.5 mr-1.5 text-[#25D366]" />WhatsApp
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-email" onClick={() => setEmailOpen(true)}>
            <Mail className="h-3.5 w-3.5 mr-1.5" />Email
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-task" onClick={() => setTaskDialogOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />Task
          </Button>
          {canReassign && (
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-reassign" onClick={() => setReassignOpen(true)}>
              Reassign
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-waiting" onClick={() => setWaitingOpen(true)}>
            Mark waiting
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-action-risk" onClick={() => move("at_risk")}>
            At risk
          </Button>
          <Select value={h.priority || "normal"} onValueChange={setPriority}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="case-action-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Link to={`/customers/${h.contact_id || contact?.contact_id}`}
            className="text-xs underline decoration-dotted text-primary ml-auto" data-testid="case-open-c360">
            Open full Customer 360 →
          </Link>
        </div>
      </Card>

      {/* ------------------- WORKFLOW SETUP REQUIRED (R4) --------------------- */}
      {(setupBlocked || h.workflow_setup) && (
        <WorkflowSetupRequiredNotice detail={setupBlocked || h.workflow_setup}
          attempted={!!setupBlocked} testId="case-workflow-setup-required" />
      )}

      {/* --------------------- BLOCKED-TRANSITION EXPLAINER ------------------- */}
      <StageGateExplanation view={gateExplain} action={gateAction}
        onClose={() => { setGateExplain(null); setGateAction(null); }}
        onOpenCase={(id) => { setGateExplain(null); navigate(`/process/${id}`); }} />
      {blocked && (
        <Card className="p-3 border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5" data-testid="case-blocked-explainer">
          <div className="flex items-start gap-2">
            <Lock className="h-4 w-4 mt-0.5 text-[hsl(var(--danger))] shrink-0" />
            <div className="text-xs space-y-1">
              <div className="font-semibold">Stage change blocked — this cannot be overridden</div>
              {blocked.payment && (
                <p className="text-muted-foreground" data-testid="case-blocked-payment">
                  {blocked.payment.label}: expected ₹{blocked.payment.expected ?? "—"}, verified ₹{blocked.payment.paid ?? "0"},
                  outstanding ₹{blocked.payment.outstanding ?? "—"}
                </p>)}
              <p className="text-muted-foreground">{blocked.message}</p>
            </div>
          </div>
        </Card>
      )}

      <Tabs value={tab} onValueChange={(v) => setTabState({ tab: v })}>
        <TabsList className="flex-wrap h-auto">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`case-tab-${k}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ------------------------------ OVERVIEW ---------------------------- */}
      {tab === "overview" && (
        <div className="space-y-3" data-testid="case-panel-overview">
          {h.next_action && (
            <NextActionCard action={h.next_action} onOpenOutcome={openOutcome} onChanged={load}
              onAssignOwner={canReassign ? () => setReassignOpen(true) : undefined} testId="case-next-action" />
          )}
          {/* The same read-only journey explanation Customer 360 shows, for this Case's opportunity. */}
          {c?.opportunity_id && <WhatHappensNextPanel opportunityId={c.opportunity_id} surface="case-overview" />}
          {governed && (
            <Panel title="Workflow — pending tasks & gates" testId="case-workflow-panel">
              {wfTasks.length === 0 && (wf?.pending_gates || []).length === 0 && (
                <p className="text-muted-foreground">Nothing pending at this stage.</p>)}
              {wfTasks.map((t) => (
                <div key={t.task_id} className="flex items-center gap-2 flex-wrap" data-testid={`case-wf-task-${t.workflow.task_key}`}>
                  <b>{t.title}</b>
                  <Badge variant="outline" className="text-[9px]">{t.workflow.stage}</Badge>
                  <Badge variant="outline" className="text-[9px]">{t.workflow.mandatory ? "Mandatory" : "Optional"}</Badge>
                  {(t.workflow.attempts || []).length > 0 && <span className="text-muted-foreground">progress saved ×{t.workflow.attempts.length}</span>}
                  <span className="text-muted-foreground">due {fmtIST(t.due_at)}</span>
                  <Button size="sm" variant="outline" className="h-6 text-[11px] ml-auto"
                    data-testid={`case-wf-open-${t.workflow.task_key}`} onClick={() => openOutcome(t.task_id)}>
                    Open outcome form
                  </Button>
                </div>
              ))}
              {(wf?.pending_gates || []).map((g) => (
                <div key={g.gate_id} className="flex items-center gap-2 flex-wrap text-muted-foreground" data-testid={`case-wf-gate-${g.kind}`}>
                  <Lock className="h-3 w-3" />
                  {g.kind === "payment" ? `Waiting for ${g.label}` : `Awaiting Leadership approval — ${g.label}`}
                  {g.to_stage ? ` before ${g.to_stage}` : ""}
                  {g.state?.message ? ` — ${g.state.message}` : ""}
                </div>
              ))}
            </Panel>
          )}
          {gate && !gate.commercial_signup_complete && (
            <Panel testId="case-signup-gate-notice">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
                <div>
                  <div className="font-semibold">Commercial signup not yet complete</div>
                  <p className="text-muted-foreground">{gate.reason}</p>
                  <p className="text-muted-foreground mt-1">
                    Approval <b>{gate.approval_type}</b> — status:{" "}
                    <b>{gate.approval_status || "not raised"}</b>. Stages still locked:{" "}
                    {(gate.stages_requiring_signup || []).join(", ") || "—"}
                  </p>
                </div>
              </div>
            </Panel>
          )}
          <Panel title="Stage rail" testId="case-stage-rail">
            <div className="flex items-center gap-1 flex-wrap">
              {(c.stages || []).map((s, i) => {
                const cur = (c.stages || []).indexOf(c.current_stage);
                const locked = stagesBlocked.has(s);
                return (
                  <span key={s} className="flex items-center gap-1">
                    <button onClick={() => stageClick(s)} disabled={locked}
                      data-testid={`case-stage-${i}`}
                      title={locked ? "Locked until commercial signup is complete" : `Move to ${s}`}
                      className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 ${
                        locked ? "opacity-50 cursor-not-allowed text-muted-foreground"
                          : i <= cur ? "bg-primary text-primary-foreground border-transparent"
                            : "text-muted-foreground hover:bg-muted"}`}>
                      {locked && <Lock className="h-3 w-3" />}{s}
                    </button>
                    {i < (c.stages || []).length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                  </span>
                );
              })}
            </div>
          </Panel>
          {c.opportunity_id && c.product_code === "SA_PREMIUM" && !governed && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {c.current_stage === "Admission" && (
                <AdmissionOutcomeActions opportunityId={c.opportunity_id}
                  contactName={h.customer_name} customerId={h.customer_id} onSaved={load} />
              )}
              {c.current_stage === "Visa" && (
                <VisaOutcomeActions opportunityId={c.opportunity_id}
                  contactName={h.customer_name} customerId={h.customer_id} onSaved={load} />
              )}
            </div>
          )}
          {data.sales_background && (
            <Panel title="Sales & consultation background (inherited)" testId="case-sales-background">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {data.sales_background.lead_source && <span>Source: <b>{data.sales_background.lead_source}</b></span>}
                <span>Sales owner: <b>{h.sales_owner_name || "Unassigned"}</b></span>
                {data.sales_background.intent && <span>Intent: <b>{data.sales_background.intent}</b></span>}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="shrink-0">Tags:</span>
                <TagsEditor entity="opportunity" recordId={c.opportunity_id}
                  tags={data.sales_background.tags || []} size="sm" />
              </div>
            </Panel>
          )}
          {(ctx?.alerts || []).length > 0 && (
            <Panel title="Alerts" testId="case-alerts">
              {(ctx.alerts || []).map((a, i) => (
                <Badge key={i} variant="outline" className="mr-1 text-[10px] bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]">{a}</Badge>
              ))}
            </Panel>
          )}
        </div>
      )}

      {/* ------------------------------- PROFILE ---------------------------- */}
      {tab === "profile" && (
        <div className="space-y-3" data-testid="case-panel-profile">
          <Panel title="Customer profile">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
              <span>Name: <b>{contact?.full_name || "—"}</b></span>
              <span>Customer ID: <b>{h.customer_id || "—"}</b></span>
              <span>Phone: <b>{contact?.primary_phone || "—"}</b></span>
              <span>Email: <b>{contact?.email || "—"}</b></span>
              <span>City: <b>{contact?.city || "—"}</b></span>
              <span>Lead source: <b>{ctx?.lead_source || "—"}</b></span>
            </div>
          </Panel>
          {Object.keys(ctx?.custom_fields || {}).length > 0 && (
            <Panel title="Product / custom fields">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                {Object.entries(ctx.custom_fields).map(([k, v]) => <span key={k}>{k}: <b>{String(v)}</b></span>)}
              </div>
            </Panel>
          )}
          {Object.keys(ctx?.form_answers || {}).length > 0 && (
            <Panel title="Lead form answers">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {Object.entries(ctx.form_answers).slice(0, 20).map(([k, v]) => (
                  <span key={k}>{k}: <b>{typeof v === "object" ? JSON.stringify(v).slice(0, 40) : String(v)}</b></span>
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ------------------------- STAGES & MILESTONES ----------------------- */}
      {tab === "stages" && (
        <div className="space-y-3" data-testid="case-panel-stages">
          <Panel title="Case stage history (append-only)">
            {(c.stage_history || []).length === 0 && <p className="text-muted-foreground">No stage moves recorded.</p>}
            {(c.stage_history || []).slice().reverse().map((s, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <span className="font-mono">{fmtIST(s.at, { year: true })}</span>
                <ArrowRight className="h-3 w-3" /><b>{s.to_stage}</b>
                <span className="text-muted-foreground">by {s.actor}</span>
                {s.override && <Badge variant="outline" className="text-[9px]">override</Badge>}
                {s.reason && <span className="text-muted-foreground">— {s.reason}</span>}
              </div>
            ))}
          </Panel>
          <Panel title="Assignment history (append-only)">
            {(c.assignment_history || []).length === 0 && <p className="text-muted-foreground">No reassignments recorded.</p>}
            {(c.assignment_history || []).slice().reverse().map((a, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <span className="font-mono">{fmtIST(a.at, { year: true })}</span>
                <span>{a.from || "Unassigned"}</span><ArrowRight className="h-3 w-3" /><b>{a.to || "Unassigned"}</b>
                <span className="text-muted-foreground">by {a.by}{a.by_role ? ` (${a.by_role})` : ""}</span>
                {a.reason && <span className="text-muted-foreground">— {a.reason}</span>}
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* -------------------------------- TASKS ----------------------------- */}
      {tab === "tasks" && (
        <div className="space-y-2" data-testid="case-panel-tasks">
          {(data.tasks || []).length === 0 && <p className="text-xs text-muted-foreground">No case tasks yet.</p>}
          {(data.tasks || []).map((t) => (
            <div key={t.task_id} className="flex items-start gap-2 text-sm p-1.5 rounded border">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={["completed", "cancelled"].includes(t.status) ? "line-through text-muted-foreground" : ""}>{t.title}</span>
                  {t.is_mandatory && <Badge variant="outline" className="text-[9px]">Mandatory</Badge>}
                  <Badge variant="outline" className="text-[9px] capitalize">{t.status}</Badge>
                  {t.cancel_reason_code === "stage_departed" && (
                    <Badge variant="outline" className="text-[9px] bg-muted" data-testid={`case-task-stage-left-${t.task_id}`}>
                      Stage left — no longer actionable
                    </Badge>)}
                  {t.workflow?.task_key && t.status === "open"
                    && (!t.workflow?.stage || c.current_stage === t.workflow.stage
                        || (wf?.workflow?.tasks?.[t.workflow.task_key]?.carry_forward)) && (
                    <Button size="sm" variant="outline" className="h-6 text-[11px]"
                      onClick={() => openOutcome(t.task_id)}>Open outcome form</Button>)}
                </div>
                {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                {t.cancel_reason_code === "stage_departed" && t.workflow?.stage_exit && (
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid={`case-task-stage-exit-${t.task_id}`}>
                    Closed {fmtIST(t.workflow.stage_exit.at)}: the Case left <b>{t.workflow.stage_exit.from_stage}</b> and
                    moved {t.workflow.stage_exit.direction === "back" ? "back" : "on"} to <b>{t.workflow.stage_exit.to_stage}</b>
                    {t.workflow.stage_exit.reason ? <> — {t.workflow.stage_exit.reason}</> : null}.
                    {" "}If the Case re-enters that stage, a fresh task is created.
                  </p>)}
              </div>
              <span className="text-[11px] text-muted-foreground shrink-0">{fmtIST(t.due_at)}</span>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="case-task-open" onClick={() => setTaskDialogOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add case task
          </Button>
        </div>
      )}

      {/* ---------------------------- COMMUNICATIONS ------------------------ */}
      {tab === "communications" && (
        <Panel title="Queued / sent communications" testId="case-panel-communications">
          {(ctx?.communications || []).length === 0 && <p className="text-muted-foreground">None queued or sent yet.</p>}
          {(ctx?.communications || []).map((m, i) => (
            <div key={i}>{m.provider} · {m.action} · <span className="capitalize">{m.status}</span>{" "}
              <span className="text-muted-foreground">({fmtIST(m.created_at)})</span></div>
          ))}
        </Panel>
      )}

      {tab === "calls" && (
        <Panel title="Calls & dispositions" testId="case-panel-calls">
          {(ctx?.calls || []).length === 0 && <p className="text-muted-foreground">No calls logged.</p>}
          {(ctx?.calls || []).map((k) => (
            <div key={k.interaction_id}><b className="capitalize">{k.outcome || "call"}</b> — {k.summary?.slice(0, 140)}{" "}
              <span className="text-muted-foreground">({fmtIST(k.created_at)})</span></div>
          ))}
        </Panel>
      )}

      {tab === "meetings" && (
        <Panel title="Consultations & meetings" testId="case-panel-meetings">
          {(ctx?.consultations || []).length === 0 && <p className="text-muted-foreground">No consultations.</p>}
          {(ctx?.consultations || []).map((a, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <span>{fmtIST(a.start, { year: true })}</span>
              <Badge variant="outline" className="text-[9px] capitalize">{a.status}</Badge>
              {a.outcome && <Badge className="text-[9px] capitalize">{a.outcome}</Badge>}
              {a.counsellor && <span className="text-muted-foreground">{a.counsellor}</span>}
              {a.feedback && <span className="text-muted-foreground">— {a.feedback.slice(0, 120)}</span>}
            </div>
          ))}
        </Panel>
      )}

      {/* -------------------------------- NOTES ----------------------------- */}
      {tab === "notes" && (
        <div className="space-y-2" data-testid="case-panel-notes">
          <div className="flex gap-2">
            <Input data-testid="case-note-input" className="h-9 text-sm"
              placeholder="Add a process/case note (append-only, authored + timestamped)…"
              value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <Button data-testid="case-note-add" size="sm" className="h-9"
              disabled={!noteText.trim()} onClick={addNote}>Add note</Button>
          </div>
          {(ctx?.notes || []).length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No notes yet.</p>}
          {(ctx?.notes || []).map((n) => (
            <div key={n.interaction_id} className="p-2 rounded-md border text-xs space-y-0.5">
              <p>{n.summary}</p>
              <p className="text-muted-foreground">{n.actor_name || n.actor} · {fmtIST(n.created_at, { year: true })}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "documents" && contact && (
        <div data-testid="case-panel-documents">
          <DocumentsWorkspace contactId={contact.contact_id} caseId={caseId} cases={[c]}
            opportunities={ctx?.opportunity ? [ctx.opportunity] : []} />
        </div>
      )}

      {tab === "info_requests" && (
        <Panel title="Outstanding information / document requests" testId="case-panel-info-requests">
          {(() => {
            const reqs = (ctx?.documents || []).filter((d) => d.status === "required");
            if (reqs.length === 0) return <p className="text-muted-foreground">Nothing outstanding — no document is in the "required" state.</p>;
            return reqs.map((d) => (
              <div key={d.document_id} className="flex items-center gap-2 flex-wrap">
                <b>{d.name}</b><Badge variant="outline" className="text-[9px]">{d.category}</Badge>
                <span className="text-muted-foreground">requested {fmtIST(d.created_at)}</span>
              </div>
            ));
          })()}
        </Panel>
      )}

      {tab === "payments" && (
        <div className="space-y-3" data-testid="case-panel-payments">
          <Panel title="Payment & milestone state">
            {!h.payment_state ? <p className="text-muted-foreground">No commercial plan on this opportunity yet.</p> : (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span>Agreed: <b>{inr(h.payment_state.totals?.total_agreed)}</b></span>
                <span>Paid: <b>{inr(h.payment_state.totals?.total_paid)}</b></span>
                <span>Outstanding: <b>{inr(h.payment_state.totals?.outstanding)}</b></span>
                {h.payment_state.npa && <Badge variant="outline" className="text-[9px]">NPA</Badge>}
              </div>
            )}
          </Panel>
          {canViewFinance && (
            <Panel title="Add-on charges raised by this Case's workflow" testId="case-addon-charges">
              {addons === null && <Skeleton className="h-10" />}
              {addons?.obligations?.length === 0 && (
                <p className="text-muted-foreground">No workflow add-on charge (e.g. an IELTS/PTE class fee) has been raised on this Case.</p>)}
              {(addons?.obligations || []).map((o) => (
                <ObligationPaymentsBlock key={o.obligation_id} obligation={o} payments={addons.payments}
                  canRecord={canRecordPayment} canVerify={canVerifyPayment}
                  onRecord={setPayFor} onVerify={verifyAddonPayment} testIdPrefix="case-addon" />
              ))}
            </Panel>
          )}
          {gate && (
            <Panel title="Commercial signup">
              <div>Signup stage: <b>{gate.signup_stage}</b></div>
              <div>Approval type: <b>{gate.approval_type}</b></div>
              <div>Approval status: <b>{gate.approval_status || "not raised"}</b></div>
              <div>Commercial signup complete: <b>{String(gate.commercial_signup_complete)}</b></div>
            </Panel>
          )}
        </div>
      )}

      <RecordObligationPaymentDialog obligation={payFor} onClose={() => setPayFor(null)}
        onRecorded={() => { loadAddons(); load(); }} />

      {tab === "tickets" && (
        <Panel title="Support tickets" testId="case-panel-tickets">
          {(ctx?.tickets || []).length === 0 && <p className="text-muted-foreground">No tickets.</p>}
          {(ctx?.tickets || []).map((t) => (
            <div key={t.ticket_id} className="flex items-center gap-2 flex-wrap">
              <span className="font-mono">{t.ticket_id}</span><b>{t.subject || t.title}</b>
              <Badge variant="outline" className="text-[9px] capitalize">{t.status}</Badge>
              <span className="text-muted-foreground">{fmtIST(t.created_at)}</span>
            </div>
          ))}
        </Panel>
      )}

      {/* ONE unified, de-duplicated Case history (backend/case_timeline.py)
          — the Audit and Stages & Milestones tabs remain as specialist raw
          views. Page is URL state so Back/refresh restores it. */}
      {tab === "timeline" && (
        <Panel title="Case timeline — everything that happened on this Case, newest first" testId="case-panel-timeline">
          <CaseTimelineFeed caseId={caseId} page={tlPage}
            onPageChange={(p) => setTabState({ tl_page: p })} testId="case-timeline" />
        </Panel>
      )}

      {tab === "audit" && (
        <Panel title="Audit trail for this Case" testId="case-panel-audit">
          {auditDenied && (
            <p className="text-muted-foreground">
              Viewing the raw audit trail requires the <b>admin.audit</b> capability,
              which your role does not hold. Stage and assignment history remain
              visible under "Stages &amp; Milestones".
            </p>
          )}
          {!auditDenied && audit === null && <Skeleton className="h-16" />}
          {!auditDenied && audit?.length === 0 && <p className="text-muted-foreground">No audit events recorded for this Case yet.</p>}
          {!auditDenied && (audit || []).map((a) => (
            <div key={a.audit_id} className="flex items-start gap-2 flex-wrap">
              <span className="font-mono shrink-0">{fmtIST(a.at, { year: true })}</span>
              <Badge variant="outline" className="text-[9px]">{a.action}</Badge>
              <span className="text-muted-foreground">by {a.actor}</span>
              {a.reason && <span className="text-muted-foreground">— {a.reason}</span>}
            </div>
          ))}
        </Panel>
      )}

      {/* ------------------------------- DIALOGS ---------------------------- */}
      {governed && openOutcomeTaskId && (
        <WorkflowOutcomeDialog caseId={caseId} taskId={openOutcomeTaskId} open
          contactId={contact?.contact_id || h.contact_id}
          onOpenChange={(o) => { if (!o) closeOutcome(); }}
          onSubmitted={() => load()} />
      )}
      <Dialog open={!!reverseTo} onOpenChange={(o) => { if (!o) setReverseTo(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Move back to {reverseTo}?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">A stage reversal needs a reason. It is audited and added to the
            Case and Customer 360 timelines; nothing already recorded is deleted.</p>
          <Textarea data-testid="case-reverse-reason" value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)} placeholder="Reason (required)" />
          <DialogFooter>
            <Button data-testid="case-reverse-submit" disabled={!reverseReason.trim()}
              onClick={async () => { const s = reverseTo; setReverseTo(null); await move(s, { reason: reverseReason.trim() }); }}>
              Move back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {contact && <QuickWhatsAppDialog contact={contact} open={waOpen} setOpen={setWaOpen} />}
      {contact && <EmailDialog contact={contact} opportunityId={c.opportunity_id} open={emailOpen} setOpen={setEmailOpen} />}
      {contact && callOpen && (
        <CallLogDialog row={{ contact_id: contact.contact_id, contact_name: contact.full_name, opportunity_id: c.opportunity_id }}
          open={callOpen} setOpen={setCallOpen} onLogged={load} />
      )}

      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add case task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label>
              <Input data-testid="case-task-title" value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} /></div>
            <div><Label className="text-xs">Description</Label>
              <Textarea data-testid="case-task-description" value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger data-testid="case-task-priority" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs">Deadline</Label>
                <Input data-testid="case-task-due" type="datetime-local" className="h-9" value={newTask.due_at}
                  onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="case-task-add" disabled={!newTask.title.trim()} onClick={addTask}>Add task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Reassign Process owner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              This changes the <b>Process owner</b> only. The Sales owner
              ({h.sales_owner_name || "Unassigned"}) is a separate field on the
              opportunity and is never modified here.
            </p>
            <div><Label className="text-xs">New Process owner</Label>
              <Select value={reassign.owner} onValueChange={(v) => setReassign({ ...reassign, owner: v })}>
                <SelectTrigger data-testid="case-reassign-owner" className="h-9"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                <SelectContent>
                  {/* Extension point: a FUTURE availability engine filters
                      this candidate list. Not implemented — no attendance
                      or leave model exists in SalesOS today. */}
                  {(users || []).map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">
              Reason {h.process_owner_user_id ? <span className="text-[hsl(var(--danger))]">(required)</span> : "(optional)"}
            </Label>
              <Textarea data-testid="case-reassign-reason" value={reassign.reason}
                onChange={(e) => setReassign({ ...reassign, reason: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="case-reassign-submit"
              disabled={!reassign.owner || (!!h.process_owner_user_id && !reassign.reason.trim())}
              onClick={doReassign}>Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={waitingOpen} onOpenChange={setWaitingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Mark Case waiting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">What is this Case waiting on?</Label>
            <Select value={waitingReason} onValueChange={setWaitingReason}>
              <SelectTrigger data-testid="case-waiting-reason" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WAITING_REASONS.map((w) => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="case-waiting-submit" onClick={async () => {
              await move("waiting_customer", { waiting_reason: waitingReason });
              setWaitingOpen(false);
            }}>Mark waiting</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
